// Cliente HTTP del frontend. Todo pasa por el proxy de Vite (/api → :4000)
// con cookies (sesión JWT httpOnly). Normaliza errores a ApiError.
import { getCountry } from "./country.ts";

const BASE = "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Json = Record<string, unknown>;

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: { Accept: "application/json", "X-Country": getCountry() },
  };
  if (body !== undefined) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(BASE + path, init);
  const data = await parse(res);

  if (!res.ok) {
    const obj = (data && typeof data === "object" ? (data as Json) : {}) as Json;
    const code = typeof obj["error"] === "string" ? (obj["error"] as string) : `http_${res.status}`;
    const message = typeof obj["message"] === "string" ? (obj["message"] as string) : defaultMessage(res.status);
    if (res.status === 401 && !path.startsWith("/auth/")) {
      // Sesión perdida → forzar login (salvo en las propias rutas de auth).
      window.dispatchEvent(new CustomEvent("tiger:unauthenticated"));
    }
    throw new ApiError(res.status, code, message, obj["details"]);
  }
  return data as T;
}

function defaultMessage(status: number): string {
  if (status === 401) return "Tu sesión expiró. Inicia sesión de nuevo.";
  if (status === 403) return "No tienes permiso para realizar esta acción.";
  if (status === 404) return "No se encontró el recurso.";
  if (status === 429) return "Demasiadas solicitudes. Intenta más tarde.";
  return "Ocurrió un error procesando la solicitud.";
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>("GET", path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>("PATCH", path, body ?? {}),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>("PUT", path, body ?? {}),
  del: <T>(path: string): Promise<T> => request<T>("DELETE", path),
};
